import Cookies from "js-cookie";
import { Dispatch } from "redux";
import {
  companyDetailsPayloadFromLoginData,
  setAuthToken,
  setCompanyDetails,
  setError,
  setJobseekerId,
  setLoading,
  setNewUser,
  setRedirectUrl,
  setRole,
  setSubscriptionStatus,
  setUser,
  setUserCredits,
  setUserCreditsLoading,
  setUserId,
} from "../reducers/authReducer";
import { AuthUser } from "../types/authTypes";
// @ts-ignore
import instance from "../../api/instance";
// @ts-ignore — apiRoutes is plain JS without declarations
import { getUserCredits } from "../../api/apiRoutes";
import { RESET_APP } from "./resetActions";
import {
  getUserRole,
  resolvePostLoginRedirect,
  ROLE_IDS,
} from "../../constants/roleConfig";
import { shouldDeferUserCreditsFetch } from "../../utils/creditsFetchPath";
import { signupConsentFields } from "../../utils/signupConsent";
import { persistor } from "../store";
import { runAsRenewalLeader } from "../../services/authSync";

// Firebase Auth is ~200 KB, and App.js imports this file, so a static import put it in
// the entry chunk for every visitor. Load it on demand and keep the module once loaded.
type FirebaseAuthModule = typeof import("../../config/firebaseConfig");
let firebaseAuth: FirebaseAuthModule | null = null;
let firebaseAuthLoading: Promise<FirebaseAuthModule> | null = null;

export const preloadFirebaseAuth = (): Promise<FirebaseAuthModule> => {
  if (firebaseAuth) return Promise.resolve(firebaseAuth);
  firebaseAuthLoading ??= import("../../config/firebaseConfig")
    .then((m) => (firebaseAuth = m))
    .catch((e) => {
      firebaseAuthLoading = null; // allow a retry on the next click
      throw e;
    });
  return firebaseAuthLoading;
};

// signInWithPopup must run in the same task as the click, or Safari/Firefox block the
// popup — so the popup thunks use `firebaseAuth ?? await …` (never `await` a loaded
// module). GoogleInUp/AppleInUp call preloadFirebaseAuth on mount to make that the case.

export const signInWithGoogle =
  (navigate: Function, redirectUrl?: string, role?: number) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));

    try {
      const { GoogleAuthProvider, signInWithPopup, auth } =
        firebaseAuth ?? (await preloadFirebaseAuth());
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(auth, provider);
      const userKey = result.user.uid;

      try {
        const existsResponse = await instance.get(
          `/accounts/check-user-exists/${userKey}/`,
        );

        if (existsResponse.data.data) {
          const requestBody = {
            user_id: userKey,
            // full_name: confirmedUser.user.displayName,
            email: result.user.email,
            // type: 1,
            // phone: confirmedUser.user.phoneNumber,
            key: userKey,
            // role: existsResponse.data.role,
          };
          const response = await instance.post(
            "/accounts/social-login/",
            requestBody,
          );

          // Token is now stored in sessionStorage via redux-persist
          dispatch(setAuthToken(response.data.data.token));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));
          dispatch(setNewUser(response.data.data.is_new_user));
          dispatch(
            setSubscriptionStatus(
              response.data.data.subscription_status ?? null,
            ),
          );
          dispatch(setUserId(response.data.data.user.id));
          {
            const cdPayload = companyDetailsPayloadFromLoginData(
              response.data.data,
            );
            if (cdPayload) dispatch(setCompanyDetails(cdPayload));
          }

          const user: AuthUser = {
            user_id: response.data.data.user.id,
            job_seeker_id: response.data.data.jobseeker_id,
            full_name: response.data.data.user.full_name,
            email: result.user.email,
            type: 1,
            country_code: null,
            phone: result.user.phoneNumber,
            key: result.user.uid,
            role: response.data.data.user.role,
            registered_company_name:
              response?.data?.data?.user.registered_company_name,
            registered_industry_name:
              response.data.data.user.registered_industry_name,
            company_id: response.data.data.company_id,
            languages: null,
            websites: null,
            profile_pic: null,
            designation: null,
            dob: null,
            gender: null,
            address: null,
            introduction: null,
            resume: response.data.data.user.resume,
            resume_json: null,
            is_active: null,
            is_deleted: null,
            profile_completed: response.data.data.profile_completed,
            first_name: response.data.data.user.first_name ?? null,
            last_name: response.data.data.user.last_name ?? null,
            groups: response.data.data.user.groups ?? null,
          };

          dispatch(setUser(user));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));
          dispatch(setNewUser(response.data.data.is_new_user));

          // Schedule token renewal for Google sign-in
          scheduleTokenRenewal(response.data.data.user.id, dispatch);

          // Jobseeker (role 2) must not keep a recruiter pricing/billing redirect
          // captured from a public pricing CTA — clear it so they land on their
          // dashboard. Other redirects (job page, invitation) are preserved.
          if (user.role === 2 && redirectUrl?.includes("from=pricing")) {
            dispatch(setRedirectUrl(null));
            redirectUrl = undefined;
          }
          resolvePostLoginRedirect(
            { ...user, is_new_user: response.data.data.is_new_user },
            redirectUrl,
            navigate,
          );
          dispatch(setLoading(false));
        } else {
          const requestBody = {
            user_id: userKey,
            full_name: result.user.displayName,
            email: result.user.email,
            type: 1,
            key: userKey,
            role: role,
            // CNS-15: sign-up opt-ins (recorded only when this creates the account).
            ...signupConsentFields(role),
          };
          const response = await instance.post(
            "/accounts/social-login/",
            requestBody,
          );

          // Token is now stored in sessionStorage via redux-persist
          dispatch(setAuthToken(response.data.data.token));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));
          dispatch(setNewUser(response.data.data.is_new_user));
          dispatch(
            setSubscriptionStatus(
              response.data.data.subscription_status ?? null,
            ),
          );
          dispatch(setUserId(response.data.data.user.id));
          {
            const cdPayload = companyDetailsPayloadFromLoginData(
              response.data.data,
            );
            if (cdPayload) dispatch(setCompanyDetails(cdPayload));
          }

          const user: AuthUser = {
            user_id: response.data.data.user.id,
            job_seeker_id: response.data.data.jobseeker_id,
            full_name: response.data.data.user.full_name,
            email: result.user.email,
            type: 1,
            country_code: null,
            phone: result.user.phoneNumber,
            key: result.user.uid,
            role: response.data.data.user.role,
            registered_company_name:
              response.data.data.user.registered_company_name,
            registered_industry_name:
              response.data.data.user.registered_industry_name,
            company_id: response.data.data.company_id,
            languages: null,
            websites: null,
            profile_pic: null,
            designation: null,
            dob: null,
            gender: null,
            address: null,
            introduction: null,
            resume: response.data.data.user.resume,
            resume_json: null,
            is_active: null,
            is_deleted: null,
            profile_completed: response.data.data.profile_completed,
            first_name: response.data.data.user.first_name ?? null,
            last_name: response.data.data.user.last_name ?? null,
            groups: response.data.data.user.groups ?? null,
          };

          dispatch(setUser(user));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));

          // Schedule token renewal for new Google sign-in users
          scheduleTokenRenewal(response.data.data.user.id, dispatch);

          // Jobseeker (role 2) must not keep a recruiter pricing/billing redirect
          // captured from a public pricing CTA — clear it so they land on their
          // dashboard. Other redirects (job page, invitation) are preserved.
          if (user.role === 2 && redirectUrl?.includes("from=pricing")) {
            dispatch(setRedirectUrl(null));
            redirectUrl = undefined;
          }
          resolvePostLoginRedirect(
            { ...user, is_new_user: response.data.data.is_new_user },
            redirectUrl,
            navigate,
          );
          dispatch(setLoading(false));
        }
        dispatch(setLoading(false));
      } catch (error) {
        console.error("Error checking user existence:", error);
        navigate("/user-role");
        dispatch(setLoading(false));
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      dispatch(
        setError(error.message || "An error occurred during Google sign-in"),
      );
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const signInWithEmailPassword =
  (email: string, password: string, navigate: Function, redirectUrl?: string) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));

    try {
      const { signInWithEmailAndPassword, auth } = await preloadFirebaseAuth();
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userKey = result.user.uid;

      // Fetch additional user data if needed
      const response = await instance.get(`/accounts/user-data/${userKey}/`);
      // Token is now stored in sessionStorage via redux-persist
      dispatch(setAuthToken(response.data.token));
      dispatch(setUserId(userKey));

      const user: AuthUser = {
        user_id: response.data.data.user.id,
        job_seeker_id: response.data.data.jobseeker_id,
        full_name: response.data.data.user.full_name,
        email: result.user.email,
        type: 1,
        country_code: null,
        phone: result.user.phoneNumber,
        key: result.user.uid,
        role: response.data.data.user.role,
        registered_company_name:
          response.data.data.user.registered_company_name,
        registered_industry_name:
          response.data.data.user.registered_industry_name,
        company_id: response.data.data.company_id,
        languages: null,
        websites: null,
        profile_pic: null,
        designation: null,
        dob: null,
        gender: null,
        address: null,
        introduction: null,
        resume: response.data.data.user.resume,
        resume_json: null,
        is_active: null,
        is_deleted: null,
        profile_completed: response.data.data.profile_completed,
        first_name: response.data.data.user.first_name ?? null,
        last_name: response.data.data.user.last_name ?? null,
        groups: response.data.data.user.groups ?? null,
      };

      dispatch(setUser(user));
      dispatch(setSubscriptionStatus(response.data.data.subscription_status ?? null));

      // Schedule token renewal for email/password sign-in
      // Note: userKey is Firebase UID, but we need the backend user ID
      // The backend user ID is in response.data.data.user.id
      const backendUserId = response.data.data?.user?.id || user.user_id;
      if (backendUserId) {
        scheduleTokenRenewal(backendUserId, dispatch);
      }

      resolvePostLoginRedirect(
        { ...user, is_new_user: response.data.data?.is_new_user },
        redirectUrl,
        navigate,
      );
    } catch (error: any) {
      console.error("Sign-in error:", error);
      let errorMessage = "Failed to sign in.";
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password"
      ) {
        errorMessage = "Incorrect email or password.";
      }
      dispatch(setError(errorMessage));
      navigate("/login");
    } finally {
      dispatch(setLoading(false));
    }
  };

export const signInWithApple =
  (navigate: Function, redirectUrl?: string, role?: number) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));

    try {
      const { OAuthProvider, signInWithPopup, auth } =
        firebaseAuth ?? (await preloadFirebaseAuth());
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(auth, provider);
      const userKey = result.user.uid;

      try {
        const existsResponse = await instance.get(
          `/accounts/check-user-exists/${userKey}/`,
        );

        if (existsResponse.data.data) {
          const requestBody = {
            user_id: userKey,
            email: result.user.email,
            key: userKey,
          };
          const response = await instance.post(
            "/accounts/social-login/",
            requestBody,
          );

          dispatch(setAuthToken(response.data.data.token));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));
          dispatch(setNewUser(response.data.data.is_new_user));
          dispatch(
            setSubscriptionStatus(
              response.data.data.subscription_status ?? null,
            ),
          );
          dispatch(setUserId(response.data.data.user.id));
          dispatch(setRole(response.data.data.user.role));
          {
            const cdPayload = companyDetailsPayloadFromLoginData(
              response.data.data,
            );
            if (cdPayload) dispatch(setCompanyDetails(cdPayload));
          }

          const user: AuthUser = {
            user_id: response.data.data.user.id,
            job_seeker_id: response.data.data.jobseeker_id,
            full_name: response.data.data.user.full_name,
            email: result.user.email,
            type: 2,
            country_code: null,
            phone: result.user.phoneNumber,
            key: result.user.uid,
            role: response.data.data.user.role,
            registered_company_name:
              response?.data?.data?.user?.registered_company_name,
            registered_industry_name:
              response.data.data.user.registered_industry_name,
            company_id: response.data.data.company_id,
            languages: null,
            websites: null,
            profile_pic: null,
            designation: null,
            dob: null,
            gender: null,
            address: null,
            introduction: null,
            resume: response.data.data.user.resume,
            resume_json: null,
            is_active: null,
            is_deleted: null,
            profile_completed: response.data.data.profile_completed,
            first_name: response.data.data.user.first_name ?? null,
            last_name: response.data.data.user.last_name ?? null,
            groups: response.data.data.user.groups ?? null,
          };
          dispatch(setUser(user));
          scheduleTokenRenewal(response.data.data.user.id, dispatch);

          // Jobseeker (role 2) must not keep a recruiter pricing/billing redirect
          // captured from a public pricing CTA — clear it so they land on their
          // dashboard. Other redirects (job page, invitation) are preserved.
          if (user.role === 2 && redirectUrl?.includes("from=pricing")) {
            dispatch(setRedirectUrl(null));
            redirectUrl = undefined;
          }
          resolvePostLoginRedirect(
            { ...user, is_new_user: response.data.data.is_new_user },
            redirectUrl,
            navigate,
          );
          dispatch(setLoading(false));
        } else {
          const requestBody = {
            user_id: userKey,
            full_name: result.user.displayName,
            email: result.user.email,
            type: 2,
            key: userKey,
            role: role ?? 1,
            // CNS-15: sign-up opt-ins (recorded only when this creates the account).
            ...signupConsentFields(role ?? 1),
          };
          const response = await instance.post(
            "/accounts/social-login/",
            requestBody,
          );
          dispatch(setAuthToken(response.data.data.token));
          dispatch(setJobseekerId(response.data.data.jobseeker_id));
          dispatch(setNewUser(response.data.data.is_new_user));
          dispatch(
            setSubscriptionStatus(
              response.data.data.subscription_status ?? null,
            ),
          );
          dispatch(setUserId(response.data.data.user.id));
          dispatch(setRole(response.data.data.user.role));
          const user: AuthUser = {
            user_id: response.data.data.user.id,
            job_seeker_id: response.data.data.jobseeker_id,
            full_name: response.data.data.user.full_name,
            email: result.user.email,
            type: 2,
            country_code: null,
            phone: result.user.phoneNumber,
            key: result.user.uid,
            role: response.data.data.user.role,
            registered_company_name:
              response.data.data.user.registered_company_name,
            registered_industry_name:
              response.data.data.user.registered_industry_name,
            company_id: response.data.data.company_id,
            languages: null,
            websites: null,
            profile_pic: null,
            designation: null,
            dob: null,
            gender: null,
            address: null,
            introduction: null,
            resume: response.data.data.user.resume,
            resume_json: null,
            is_active: null,
            is_deleted: null,
            profile_completed: response.data.data.profile_completed,
            first_name: response.data.data.user.first_name ?? null,
            last_name: response.data.data.user.last_name ?? null,
            groups: response.data.data.user.groups ?? null,
          };
          dispatch(setUser(user));
          scheduleTokenRenewal(response.data.data.user.id, dispatch);

          // Jobseeker (role 2) must not keep a recruiter pricing/billing redirect
          // captured from a public pricing CTA — clear it so they land on their
          // dashboard. Other redirects (job page, invitation) are preserved.
          if (user.role === 2 && redirectUrl?.includes("from=pricing")) {
            dispatch(setRedirectUrl(null));
            redirectUrl = undefined;
          }
          resolvePostLoginRedirect(
            { ...user, is_new_user: response.data.data.is_new_user },
            redirectUrl,
            navigate,
          );
          dispatch(setLoading(false));
        }
      } catch (error) {
        console.error("Error checking user existence (Apple):", error);
        navigate("/user-role");
        dispatch(setLoading(false));
      }
    } catch (error: any) {
      dispatch(setError(error.message || "An error occurred"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

// export const signInWithPhoneNumber =
//   (navigate: Function) => async (dispatch: Dispatch) => {
//     dispatch(setLoading(true));
//     try {
//     } catch (error: any) {
//       console.error("Google sign-in error:", error);
//       dispatch(
//         setError(error.message || "An error occurred during Google sign-in")
//       );
//     } finally {
//       dispatch(setLoading(false));
//     }
//   };

/** Loads credit balance from GET /accounts/user-credits/ into auth.userCredits. No-op for jobseekers. */
export const fetchUserCredits =
  () =>
  async (
    dispatch: Dispatch,
    getState: () => {
      auth: { user: AuthUser | null; userCredits: number | null };
    },
  ) => {
    const { auth } = getState();
    const user = auth?.user;
    const role = getUserRole(user);
    if (role == null || role === ROLE_IDS.APPLICANT) return;
    if (
      typeof window !== "undefined" &&
      shouldDeferUserCreditsFetch(window.location.pathname)
    ) {
      return;
    }
    const hasCachedCredits = auth?.userCredits != null;
    if (!hasCachedCredits) {
      dispatch(setUserCreditsLoading(true));
    }
    try {
      const response = await instance.get(getUserCredits);
      const credits = response?.data?.data?.credits;
      if (typeof credits === "number" && !Number.isNaN(credits)) {
        dispatch(setUserCredits(credits));
      }
    } catch (error) {
      console.error("fetchUserCredits failed:", error);
    } finally {
      dispatch(setUserCreditsLoading(false));
    }
  };

export const signOut =
  () =>
  (dispatch: Dispatch): void => {
    // Clear token renewal intervals on logout
    clearTokenRenewalIntervals();
    unregisterSSEAbort();

    dispatch({ type: RESET_APP }); // This will reset the entire state

    // Clear persisted session immediately so axios / getAuthTokenFromClient cannot
    // read a stale token from sessionStorage before the page unloads (avoids a
    // trailing 401 opening "Session Expired" right after logout).
    // RESET_APP above already told the other tabs to log out (services/authSync).
    sessionStorage.clear();
    window.location.replace("/");
  };

// Store interval IDs to allow cleanup
let tokenRenewalIntervals: NodeJS.Timeout[] = [];
// Gives up this tab's place as (or in the queue for) renewal leader.
let stopRenewalLeadership: (() => void) | null = null;

// Clear all token renewal intervals (useful for logout)
export const clearTokenRenewalIntervals = () => {
  stopRenewalLeadership?.();
  stopRenewalLeadership = null;
  tokenRenewalIntervals.forEach((interval) => clearInterval(interval));
  tokenRenewalIntervals = [];
};

// SSE abort registry — allows forceLogoutForRoleChange to kill SSE instantly
let globalSSEAbort: (() => void) | null = null;

export const registerSSEAbort = (abortFn: () => void): void => {
  globalSSEAbort = abortFn;
};

export const unregisterSSEAbort = (): void => {
  globalSSEAbort = null;
};

export const renewToken = async (_userId: string, dispatch: Dispatch) => {
  try {
    // The backend renews the caller's own token (from the auth header); user_id is ignored.
    const response = await instance.post(`/accounts/token/renew/`);
    // The token comes back at the top level; older shapes nested it under data.
    const newToken = response.data?.token ?? response.data?.data?.token;
    // Token is now stored in sessionStorage via redux-persist
    if (newToken) dispatch(setAuthToken(newToken));
  } catch (error: any) {
    console.error("Token renewal failed:", error);
    // If renewal fails, don't show error to user - let the 401 handler deal with it
    // dispatch(setError(error.message || "Failed to renew token"));
  }
};

export const scheduleTokenRenewal = (userId: string, dispatch: Dispatch) => {
  // Clear any existing intervals first
  clearTokenRenewalIntervals();

  // Every tab shares one session, so only the leader tab renews; the new token
  // reaches the other tabs through authSync. The next tab in line takes over
  // when the leader closes.
  stopRenewalLeadership = runAsRenewalLeader(() => {
    // Schedule token renewal every 5 hours (18000000 ms)
    const interval = setInterval(() => renewToken(userId, dispatch), 18000000);
    tokenRenewalIntervals.push(interval);
    return () => clearInterval(interval);
  });

  // Also renew immediately if needed (optional - can be removed if not desired)
  // renewToken(userId, dispatch);
};
// function getState() {
//   throw new Error("Function not implemented.");
// }

export const forceLogoutForRoleChange =
  (navigate?: (path: string) => void) =>
  async (dispatch: Dispatch): Promise<void> => {
    clearTokenRenewalIntervals();

    // ← Kill SSE immediately before anything else
    if (globalSSEAbort) {
      globalSSEAbort();
      globalSSEAbort = null;
    }

    await persistor.flush();

    dispatch({ type: RESET_APP }); // also logs the other tabs out (authSync)

    sessionStorage.clear();

    if (navigate) {
      navigate("/");
    } else {
      window.location.href = "/";
    }
  };
